import { useState, useEffect, useRef } from 'react';
import APIService from '../services/api';
import ReactMarkdown from 'react-markdown';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { 
  FileText, 
  RotateCcw, 
  Download, 
  FileSpreadsheet, 
  Layers,
  FileCheck2
} from 'lucide-react';

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const pdfTemplateRef = useRef(null);
  const [activeReportForPdf, setActiveReportForPdf] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  async function loadReports() {
    try {
      const data = await APIService.getReports();
      setReports(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleDownload = async (report, format) => {
    setDownloading(`${report.id}-${format}`);
    
    try {
      if (format === 'CSV') {
        const content = 'Report ID,Report Name,Category,Date,Content\n' +
                  `${report.id},"${report.name}","${report.type}",${new Date(report.created_at).toISOString().split('T')[0]},"${report.content.replace(/"/g, '""')}"`;
        const blob = new Blob([content], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `AAIA_${report.name.replace(/\s+/g, '_')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (format === 'PDF') {
        // Set active report to render it in the hidden div
        setActiveReportForPdf(report);
        // Wait for state to update and DOM to render
        await new Promise(resolve => setTimeout(resolve, 800)); // slightly longer wait to ensure markdown is rendered
        
        if (pdfTemplateRef.current) {
          const canvas = await html2canvas(pdfTemplateRef.current, { scale: 2, useCORS: true });
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: [canvas.width / 2, canvas.height / 2]
          });
          pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
          pdf.save(`AAIA_${report.name.replace(/\s+/g, '_')}.pdf`);
        }
        setActiveReportForPdf(null);
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to generate download');
    } finally {
      setDownloading(null);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background: 'transparent' }}>
      
      {/* Hidden PDF Template */}
      <div style={{ position: 'fixed', top: 0, left: '-2000px', width: '800px', background: '#fff', padding: '40px', color: '#000', fontFamily: 'Inter, sans-serif' }} ref={pdfTemplateRef}>
        {activeReportForPdf && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30, borderBottom: '2px solid #eee', paddingBottom: 20 }}>
              <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#111' }}>Automotive AI Agent</h1>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>{activeReportForPdf.type} Report</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{new Date(activeReportForPdf.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            <h2 style={{ fontSize: 20, marginBottom: 20, color: '#222' }}>{activeReportForPdf.name}</h2>
            <div className="markdown-body" style={{ color: '#333', lineHeight: 1.6, fontSize: 14 }}>
              <ReactMarkdown>{activeReportForPdf.content}</ReactMarkdown>
            </div>
            <div style={{ marginTop: 50, borderTop: '1px solid #eee', paddingTop: 20, fontSize: 11, color: '#888', textAlign: 'center' }}>
              Generated by Achtrex LLC - AAIA Platform • Confidential
            </div>
          </div>
        )}
      </div>

      <div style={{ height:64, padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(255,255,255,0.7)', backdropFilter:'blur(16px)', borderBottom:'1px solid var(--bord)', flexShrink:0, zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:15, fontWeight:600, color:'var(--dgray)' }}>
          <div style={{ width:36, height:36, background:'linear-gradient(135deg, var(--dblu), var(--mid))', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', boxShadow:'var(--shadow-md)' }}>
            <FileCheck2 size={18} />
          </div>
          Reports Center
        </div>
      </div>

      <div style={{ flex:1, padding:24, overflowY:'auto' }}>
        <div className="card animate-fade-in" style={{ padding: 24, background: 'var(--white)' }}>
          <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--dgray)', marginBottom: 4 }}>Generated Reports</h2>
              <p style={{ fontSize: 13, color: 'var(--sgray)' }}>Access and download historical analysis and diagnostic reports.</p>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn-outline" onClick={loadReports} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <RotateCcw size={14} /> Refresh
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--lgray)', color: 'var(--mgray)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <th style={{ padding: '12px 16px', fontWeight: 600 }}>Report Name</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600 }}>Category</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600 }}>Generated Date</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600 }}>Size</th>
                  <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="5" style={{ textAlign: 'center', padding: 24 }}><span className="loading-dot" /> Loading reports...</td></tr>}
                {!loading && reports.length === 0 && (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: 40, color: 'var(--mgray)' }}>No reports generated yet. Run an analysis in the Fleet or Diagnostics tab.</td></tr>
                )}
                {!loading && reports.map(report => (
                  <tr key={report.id} style={{ borderBottom: '1px solid var(--lgray)', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'var(--offwh)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '16px', fontSize: 14, fontWeight: 500, color: 'var(--dgray)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FileText size={16} style={{ color: 'var(--teal)' }} />
                        {report.name}
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span className="pill" style={{ background: 'var(--offwh)', color: 'var(--mgray)', border: '1px solid var(--lgray)' }}>{report.type}</span>
                    </td>
                    <td style={{ padding: '16px', fontSize: 13, color: 'var(--mgray)' }}>{new Date(report.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '16px', fontSize: 13, color: 'var(--sgray)' }}>{formatBytes(report.size_bytes)}</td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button 
                          className="btn-outline" 
                          onClick={() => handleDownload(report, 'PDF')}
                          disabled={downloading !== null}
                          style={{ padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          {downloading === `${report.id}-PDF` ? <span className="loading-dot" /> : <><Download size={13} /> PDF</>}
                        </button>
                        <button 
                          className="btn-outline" 
                          onClick={() => handleDownload(report, 'CSV')}
                          disabled={downloading !== null}
                          style={{ padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          {downloading === `${report.id}-CSV` ? <span className="loading-dot" /> : <><Download size={13} /> CSV</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
